import Backdrop from '@/components/ui/backdrop';
import { Location, SingleLocation } from '@/core/entities';
import LocationsManagement from '@/features/locations/locations-management/LocationsManagement';
import { useLocation } from '@/queries/locations';
import { useSite } from '@/queries/sites';
import { PAGINATION_META_DEFAULT } from '@/shared/constants/pagination';
import { useSearchQuery } from '@/shared/hooks/useSearchQuery';
import { PaginationMeta } from '@/shared/types/pagination';
import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import { Box, Paper } from '@mui/material';
import { FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const LocationPage: FunctionComponent = (): ReactElement => {
	const { locationId } = useParams();
	const navigate = useNavigate();

	const [location, setLocation] = useState<SingleLocation>();
	const [subLocations, setSubLocations] = useState<Location[]>([]);
	const { name, site, ancestors, subLevel } = location || {};

	const [paginationMeta, setPaginationMeta] = useState<PaginationMeta>(PAGINATION_META_DEFAULT);
	const { currentPage } = paginationMeta;

	const [searchTerm] = useState('');
	const searchQuery = useSearchQuery(searchTerm);

	const { query: locationQuery } = useLocation({
		locationId,
		queryParams: { limit: 99999, search: searchQuery },
	});
	const { data: locationData } = locationQuery;
	const { pages } = locationData || {};

	const { query: siteQuery } = useSite(site?.id);
	const { data: singleSite } = siteQuery;

	useEffect(() => {
		const location = pages?.[currentPage - 1];
		if (location) setLocation({ ...location });
	}, [pages, currentPage]);

	useEffect(() => {
		const { subLocations } = pages?.find((page) => page.subLocations.page === currentPage) || {};
		if (subLocations) {
			const {
				data,
				page: currentPage,
				totalItems,
				totalPages,
				itemsPerPage: pageSize,
			} = subLocations;
			setSubLocations(data);
			setPaginationMeta({ currentPage, totalItems, totalPages, pageSize });
		}
	}, [pages, currentPage]);

	const breadcrumbs = useMemo(() => {
		if (!ancestors || !site) return [];
		return [
			{ title: site.name, path: '/locations' },
			...[...ancestors].reverse().map((ancestor, key) => ({
				title: ancestor.name,
				path: key === ancestors.length - 1 ? undefined : `/locations/${ancestor.id}`,
			})),
		];
	}, [ancestors, site]);

	if (!name || !subLevel || !singleSite) return <Backdrop />;

	return (
		<AdminPanelPage title={name} breadcrumbs={breadcrumbs}>
			<Box component={Paper} padding={3}>
				<LocationsManagement
					level={subLevel}
					site={singleSite}
					header={{}}
					table={{
						data: subLocations,
						rowAction: (location) => navigate(`/locations/${location.id}`),
					}}
				/>
			</Box>
		</AdminPanelPage>
	);
};

export default LocationPage;
